/**
 * Equipment → My requests: the holder's view, designed for someone who
 * has never used the tool.
 *
 *   Waiting for you   — only what needs an action, one sentence + one button
 *   Current bookings  — one card per booking with a plain "what happens
 *                       next" line and verbs for buttons
 *   Past bookings     — collapsed
 *
 * Every holder action is its own small dialog with one job and one
 * primary button (Change return date · Someone else collects · Hand over
 * to a colleague · Edit purpose). The old all-in-one Edit modal mixed
 * them and hid the hand-over behind a secondary button nobody pressed.
 */
import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { isDemoMode } from "../state/store";
import { displayName } from "../utils/names";
import { restRpc } from "../utils/withRetry";
import { toast } from "../utils/toast";
import { isWeekend } from "../utils/dates";
import { getHoliday } from "../utils/holidays";
import {
  dateProblem,
  timeSlots,
  transferWaitingOn,
  type EquipmentBooking,
  type EquipmentBookingLine,
  type EquipmentItem,
  type EquipmentTransfer,
} from "../utils/equipment";
import { PersonSelect, backdropHandlers, fmtDate } from "./equipmentShared";
import { TransferBanner } from "./EquipmentTransfer";

type Dialog =
  | { kind: "date"; b: EquipmentBooking }
  | { kind: "collector"; b: EquipmentBooking }
  | { kind: "handover"; b: EquipmentBooking }
  | { kind: "notes"; b: EquipmentBooking };

export function MyRequests({
  bookings, transfers, items, linesFor, me, profileId, isAdmin, closures,
  renderCard, cancelBooking, afterAction,
}: {
  bookings: EquipmentBooking[];
  transfers: EquipmentTransfer[];
  items: EquipmentItem[];
  linesFor: (id: number) => EquipmentBookingLine[];
  me: string;
  profileId: string | null;
  isAdmin: boolean;
  closures: Record<string, string>;
  /** The shared booking card (Equipment.tsx BookingList) for one booking. */
  renderCard: (b: EquipmentBooking, actions: JSX.Element | null) => JSX.Element;
  cancelBooking: (b: EquipmentBooking) => Promise<void>;
  afterAction: () => Promise<void>;
}): JSX.Element {
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [showPast, setShowPast] = useState(false);
  const demo = isDemoMode.value;

  const isMe = (name: string): boolean => !!me && name === me;
  const transferToMe = (b: EquipmentBooking): EquipmentTransfer | null =>
    transfers.find((t) => t.booking_id === b.id && t.status === "PENDING"
      && (demo ? t.to_name === me : t.to_profile_id === profileId)) ?? null;
  const isHolder = (b: EquipmentBooking): boolean =>
    (profileId && b.requester_id ? b.requester_id === profileId : b.requester_name === me) || isMe(b.on_behalf_of);
  const canAct = (b: EquipmentBooking): boolean => isAdmin || isHolder(b);

  const mine = bookings.filter((b) => isHolder(b) || isMe(b.pickup_delegate ?? "") || !!transferToMe(b));
  const active = mine.filter((b) => b.status === "PENDING" || b.status === "APPROVED" || b.status === "COLLECTED" || b.status === "RETURN_PENDING");
  const past = mine.filter((b) => !active.includes(b));

  /* ---- things waiting for me ---- */
  const waiting: JSX.Element[] = [];
  for (const b of active) {
    const t = transferToMe(b);
    if (t && !t.accepted_at) {
      waiting.push(
        <li key={`t${b.id}`} class="eq-wait">
          <div class="eq-wait-text">
            <b>{displayName(t.from_name)} wants to hand {b.reference} over to you</b>
            <span>{lineSummary(linesFor(b.id), items)} · due back {fmtDate(b.return_date)}. Accepting makes the booking, and the kit, yours.</span>
          </div>
          <TransferBanner transfer={t} me={me} profileId={profileId} isAdmin={isAdmin} onChanged={afterAction} compact />
        </li>,
      );
    }
    if (b.status === "RETURN_PENDING" && isHolder(b)) {
      waiting.push(
        <li key={`r${b.id}`} class="eq-wait">
          <div class="eq-wait-text">
            <b>Confirm the return report for {b.reference}</b>
            <span>General Affairs declared what came back. Read it and confirm, or dispute it if something is wrong.</span>
          </div>
          <button type="button" class="eq-action eq-action-primary" onClick={() => scrollToCard(b.id)}>Open the report</button>
        </li>,
      );
    }
    if ((b.status === "PENDING" || b.status === "APPROVED") && isMe(b.pickup_delegate ?? "") && !isHolder(b)) {
      waiting.push(
        <li key={`d${b.id}`} class="eq-wait eq-wait-info">
          <div class="eq-wait-text">
            <b>You collect {b.reference} for {displayName(b.on_behalf_of || b.requester_name)}</b>
            <span>{b.status === "APPROVED" ? "Approved — " : "Once approved, "}collect it from General Affairs on {fmtDate(b.pickup_date)} at {b.pickup_time}. The booking stays theirs.</span>
          </div>
        </li>,
      );
    }
  }

  const actionsFor = (b: EquipmentBooking): JSX.Element | null => {
    if (!canAct(b)) return null;
    const open = b.status === "PENDING" || b.status === "APPROVED";
    const out = b.status === "COLLECTED";
    if (!open && !out) return null;
    return (
      <>
        <button type="button" class="eq-action" onClick={() => setDialog({ kind: "date", b })}>Change return date</button>
        {open && <button type="button" class="eq-action" onClick={() => setDialog({ kind: "collector", b })}>Someone else collects</button>}
        <button type="button" class="eq-action" onClick={() => setDialog({ kind: "handover", b })}>Hand over to a colleague</button>
        <button type="button" class="eq-action eq-action-quiet" onClick={() => setDialog({ kind: "notes", b })}>Edit purpose</button>
        {open && <button type="button" class="eq-action eq-action-quiet eq-action-danger" onClick={() => void cancelBooking(b)}>Cancel request</button>}
      </>
    );
  };

  const close = (): void => setDialog(null);
  const done = async (): Promise<void> => { setDialog(null); await afterAction(); };

  return (
    <div class="eq-mine">
      {waiting.length > 0 && (
        <section class="eq-waiting" aria-label="Waiting for you">
          <h3 class="eq-section-title">Waiting for you</h3>
          <ul class="eq-wait-list">{waiting}</ul>
        </section>
      )}

      <section aria-label="Current bookings">
        <h3 class="eq-section-title">Current bookings</h3>
        {active.length === 0 ? (
          <div class="eq-empty">
            Nothing on the go. Need a camera, a tripod or a microphone? Go to <b>Book</b>.
          </div>
        ) : (
          <ul class="eq-booking-list">{active.map((b) => renderCard(b, actionsFor(b)))}</ul>
        )}
      </section>

      {past.length > 0 && (
        <section aria-label="Past bookings" class="eq-past">
          <button type="button" class="eq-action eq-action-quiet" aria-expanded={showPast} onClick={() => setShowPast((v) => !v)}>
            {showPast ? "Hide" : "Show"} {past.length} past booking{past.length === 1 ? "" : "s"}
          </button>
          {showPast && <ul class="eq-booking-list">{past.map((b) => renderCard(b, null))}</ul>}
        </section>
      )}

      {dialog?.kind === "date" && <ReturnDateDialog booking={dialog.b} closures={closures} onClose={close} onDone={done} />}
      {dialog?.kind === "collector" && <CollectorDialog booking={dialog.b} me={me} onClose={close} onDone={done} />}
      {dialog?.kind === "handover" && (
        <HandOverDialog
          booking={dialog.b}
          pending={transfers.find((t) => t.booking_id === dialog.b.id && t.status === "PENDING") ?? null}
          me={me}
          onClose={close}
          onDone={done}
        />
      )}
      {dialog?.kind === "notes" && <NotesDialog booking={dialog.b} onClose={close} onDone={done} />}
    </div>
  );
}

function scrollToCard(id: number): void {
  document.getElementById(`eq-booking-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
}

/** "1× Cellulare Samsung, 2× Microfoni Boya" */
export function lineSummary(lines: EquipmentBookingLine[], items: EquipmentItem[]): string {
  return lines
    .map((l) => `${l.qty_approved ?? l.qty_requested}× ${items.find((i) => i.id === l.item_id)?.name ?? "item"}`)
    .join(", ");
}

/* ─────────────────── one dialog per action ───────────────────────── */

function ActionDialog({
  title, sub, children, onClose, primary, onPrimary, busy, disabled,
}: {
  title: string;
  sub?: string;
  children: JSX.Element | JSX.Element[];
  onClose: () => void;
  primary: string;
  onPrimary: () => void;
  busy: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div class="modal-backdrop open" role="presentation" {...backdropHandlers(onClose)}>
      <div class="modal eq-dialog" role="dialog" aria-modal="true" aria-labelledby="eqDialogTitle" tabindex={-1} onClick={(e) => e.stopPropagation()}>
        <h3 id="eqDialogTitle">{title}</h3>
        {sub && <div class="modal-date">{sub}</div>}
        {children}
        <div class="modal-actions">
          <button type="button" class="modal-secondary" onClick={onClose}>Close</button>
          <button type="button" class="modal-primary" disabled={busy || disabled} onClick={onPrimary}>{busy ? "Saving…" : primary}</button>
        </div>
      </div>
    </div>
  );
}

function useRpc(): [boolean, (fn: () => Promise<void>, demoMsg: string) => Promise<boolean>] {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>, demoMsg: string): Promise<boolean> => {
    if (isDemoMode.value) { toast(demoMsg); return true; }
    setBusy(true);
    try {
      await fn();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : "Something went wrong");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

function ReturnDateDialog({ booking: b, closures, onClose, onDone }: {
  booking: EquipmentBooking; closures: Record<string, string>; onClose: () => void; onDone: () => Promise<void>;
}): JSX.Element {
  const [date, setDate] = useState(b.return_date);
  const [time, setTime] = useState(b.return_time);
  const [busy, run] = useRpc();
  const deps = { isWeekend, getHolidayName: (iso: string) => getHoliday(iso)?.name ?? null, closures };
  const problem = dateProblem(date, "", deps) ?? (date < b.pickup_date ? "The return can't be before the pickup" : null);
  const unchanged = date === b.return_date && time === b.return_time;
  const save = async (): Promise<void> => {
    if (problem || unchanged) return;
    const ok = await run(
      () => restRpc("equipment_update_return_date", { p_booking_id: b.id, p_return_date: date, p_return_time: time }).then(() => undefined),
      "Demo mode — nothing is saved.",
    );
    if (ok) { toast(`${b.reference}: return now ${fmtDate(date)}${time ? ` ${time}` : ""}`); await onDone(); }
  };
  return (
    <ActionDialog title="Change the return date" sub={`${b.reference} · currently due back ${fmtDate(b.return_date)}${b.return_time ? ` ${b.return_time}` : ""}`}
      onClose={onClose} primary="Save new date" onPrimary={() => void save()} busy={busy} disabled={!!problem || unchanged}>
      <div class="eq-form-grid">
        <label class="eq-field">
          <span>New return date</span>
          <input type="date" value={date} min={b.pickup_date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
          {problem && <em class="eq-field-err">{problem}</em>}
        </label>
        <label class="eq-field">
          <span>Time <i>(optional)</i></span>
          <select value={time} onChange={(e) => setTime((e.target as HTMLSelectElement).value)}>
            <option value="">—</option>
            {timeSlots().map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
      <p class="eq-dialog-hint">Weekdays only. If the kit is still reserved for you, the items must be free until the new date. General Affairs is told by email.</p>
    </ActionDialog>
  );
}

function CollectorDialog({ booking: b, me, onClose, onDone }: {
  booking: EquipmentBooking; me: string; onClose: () => void; onDone: () => Promise<void>;
}): JSX.Element {
  const holder = b.on_behalf_of || b.requester_name;
  const [who, setWho] = useState(b.pickup_delegate ?? "");
  const [busy, run] = useRpc();
  const unchanged = who === (b.pickup_delegate ?? "");
  const save = async (): Promise<void> => {
    if (unchanged) return;
    const ok = await run(
      () => restRpc("equipment_set_pickup_delegate", { p_booking_id: b.id, p_name: who }).then(() => undefined),
      "Demo mode — nothing is saved.",
    );
    if (ok) { toast(who ? `${displayName(who)} will collect ${b.reference} — they have been emailed` : `${b.reference}: you collect it yourself`); await onDone(); }
  };
  return (
    <ActionDialog title="Who collects the kit?" sub={`${b.reference} · pickup ${fmtDate(b.pickup_date)} at ${b.pickup_time}`}
      onClose={onClose} primary={who ? "Confirm" : "I collect it myself"} onPrimary={() => void save()} busy={busy} disabled={unchanged}>
      <div class="eq-form-grid">
        <PersonSelect label="Colleague who will collect it" value={who} onChange={setWho} exclude={[holder, me].filter(Boolean)} placeholder="— I collect it myself —" wide />
      </div>
      <p class="eq-dialog-hint">
        Just a heads-up for General Affairs: they see this name at pickup and the colleague gets an email.
        The booking, and the responsibility for the kit, stay with {displayName(holder)}. To pass the booking itself to someone, use <b>Hand over</b> instead.
      </p>
    </ActionDialog>
  );
}

function HandOverDialog({ booking: b, pending, me, onClose, onDone }: {
  booking: EquipmentBooking; pending: EquipmentTransfer | null; me: string; onClose: () => void; onDone: () => Promise<void>;
}): JSX.Element {
  const holder = b.on_behalf_of || b.requester_name;
  const [to, setTo] = useState("");
  const [busy, run] = useRpc();
  const propose = async (): Promise<void> => {
    if (!to) return;
    const ok = await run(
      () => restRpc("equipment_propose_transfer", { p_booking_id: b.id, p_to_name: to }).then(() => undefined),
      "Demo mode — nothing is saved.",
    );
    if (ok) { toast(`Hand-over proposed — ${displayName(to)} and General Affairs have been emailed`); await onDone(); }
  };
  const withdraw = async (): Promise<void> => {
    if (!pending || !confirm(`Withdraw the hand-over to ${displayName(pending.to_name)}?`)) return;
    const ok = await run(
      () => restRpc("equipment_decline_transfer", { p_transfer_id: pending.id, p_reason: "" }).then(() => undefined),
      "Demo mode — nothing is saved.",
    );
    if (ok) { toast("Hand-over withdrawn"); await onDone(); }
  };
  if (pending) {
    const waiting = transferWaitingOn(pending);
    return (
      <ActionDialog title="Hand-over in progress" sub={`${b.reference} → ${displayName(pending.to_name)}`}
        onClose={onClose} primary="Withdraw the hand-over" onPrimary={() => void withdraw()} busy={busy}>
        <p class="eq-dialog-hint">
          Proposed {fmtDate(pending.proposed_at)}. {pending.accepted_at ? `${displayName(pending.to_name)} has accepted` : `${displayName(pending.to_name)} has not answered yet`};
          {pending.approved_at ? " General Affairs has approved." : " General Affairs has not approved yet."} It becomes effective when both have said yes{waiting ? ` (still waiting for: ${waiting})` : ""}.
          Until then the booking stays with {displayName(holder)}.
        </p>
      </ActionDialog>
    );
  }
  return (
    <ActionDialog title="Hand the booking over to a colleague" sub={`${b.reference} · now held by ${displayName(holder)}`}
      onClose={onClose} primary="Propose the hand-over" onPrimary={() => void propose()} busy={busy} disabled={!to}>
      <div class="eq-form-grid">
        <PersonSelect label="New holder" value={to} onChange={setTo} exclude={[holder, me].filter(Boolean)} placeholder="— choose a colleague —" wide />
      </div>
      <p class="eq-dialog-hint">
        The booking and the responsibility for the kit pass to the colleague. They and General Affairs are emailed now and must <b>both</b> accept; until then nothing changes.
        If someone just picks the kit up for you, use <b>Someone else collects</b> instead.
      </p>
    </ActionDialog>
  );
}

function NotesDialog({ booking: b, onClose, onDone }: {
  booking: EquipmentBooking; onClose: () => void; onDone: () => Promise<void>;
}): JSX.Element {
  const [purpose, setPurpose] = useState(b.purpose);
  const [notes, setNotes] = useState(b.requester_notes);
  const [busy, run] = useRpc();
  const unchanged = purpose.trim() === b.purpose && notes.trim() === b.requester_notes;
  const save = async (): Promise<void> => {
    if (unchanged) return;
    const ok = await run(
      () => restRpc("equipment_update_booking_notes", { p_booking_id: b.id, p_purpose: purpose.trim(), p_notes: notes.trim() }).then(() => undefined),
      "Demo mode — nothing is saved.",
    );
    if (ok) { toast(`${b.reference} updated`); await onDone(); }
  };
  return (
    <ActionDialog title="Purpose and notes" sub={b.reference} onClose={onClose} primary="Save" onPrimary={() => void save()} busy={busy} disabled={unchanged}>
      <div class="eq-form-grid">
        <label class="eq-field eq-field-wide">
          <span>Purpose / project</span>
          <input type="text" value={purpose} maxLength={120} onInput={(e) => setPurpose((e.target as HTMLInputElement).value)} />
        </label>
        <label class="eq-field eq-field-wide">
          <span>Notes for General Affairs</span>
          <textarea value={notes} maxLength={400} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
        </label>
      </div>
      <p class="eq-dialog-hint">Items, quantities and the pickup date can't be changed here: cancel the request and book again.</p>
    </ActionDialog>
  );
}
